// Mocks for the Datastore integration tests
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Collections } from '@freelancer/datastore/collections.model';
import * as Rx from 'rxjs';
import {
  DatastoreCollection,
  DatastoreDocument,
  Reference,
  RequestStatus,
  StoreBackend,
  SuccessResponseData,
} from '..';
import {
  BackendDeleteReturnTypes,
  BackendFetchReturnTypes,
  BackendPushReturnTypes,
  BackendSetReturnTypes,
  BackendUpdateReturnTypes,
} from '../../backend.model';

/**
 * MockObject is a fake model that can be extended with mock fields in order to
 * support the Datastore tests
 */
export interface MockObject {
  readonly id: number;
  readonly username: string;
  readonly jobs?: ReadonlyArray<string>;
  readonly context?: { readonly type: string; readonly id: number };
  readonly isEmployer?: boolean;
  readonly rating: number;
}

export interface MockObjectGetResultApi {
  readonly mockObjects: { readonly [k in string]?: MockObject };
}

export interface MockObjectPostRawPayload {
  readonly name: string;
}

export function makeMockObject({
  id,
  username = 'mockObject',
  jobs = ['js', 'html', 'css'],
  rating = 10,
}: {
  readonly id: string | number;
  readonly username?: string;
  readonly jobs?: ReadonlyArray<string>;
  readonly rating?: number;
}): MockObject {
  return {
    id: Number(id),
    username,
    jobs,
    rating,
  };
}

/**
 * Mock response from API must have `status` and `result` fields.
 * The request is originally made in freelancer-http.service, which is the
 * location where Angular's native HTTP methods are called.
 */
export function getMockObjectFetchResponse({
  ids,
  username = 'mockObject',
  jobs = ['js', 'html', 'css'],
  rating = 10,
}: {
  readonly ids: ReadonlyArray<string | number>;
  readonly username?: string;
  readonly jobs?: ReadonlyArray<string>;
  readonly rating?: number;
}): SuccessResponseData<BackendFetchReturnTypes['testing']> {
  return {
    result: {
      mockObjects: ids.reduce(
        (acc, id) => ({
          ...acc,
          [id]: makeMockObject({ id, username, jobs, rating }),
        }),
        {},
      ),
    },
    status: 'success',
  };
}

// Mock responses for the rest of the CRUD operations
export function getMockObjectPushResponse(): SuccessResponseData<
  BackendPushReturnTypes['testing']
> {
  return {
    result: {},
    status: 'success',
  };
}

export function getMockObjectSetResponse(): SuccessResponseData<
  BackendSetReturnTypes['testing']
> {
  return {
    result: {},
    status: 'success',
  };
}

export function getMockObjectUpdateResponse(): SuccessResponseData<
  BackendUpdateReturnTypes['testing']
> {
  return {
    result: {},
    status: 'success',
  };
}

export function getMockObjectDeleteResponse(): SuccessResponseData<
  BackendDeleteReturnTypes['testing']
> {
  return {
    result: {},
    status: 'success',
  };
}

// api-http.service mock
@Injectable()
export class MockApiHttp {
  // inject Angular's HttpClient in place of our FreelancerHttp service
  constructor(private http: HttpClient) {}

  fetch() {
    return this.http.get('');
  }

  post() {
    return this.http.post('', {});
  }

  put() {
    return this.http.put('', {});
  }

  delete() {
    return this.http.delete('');
  }
}

export class DatastoreCollectionMock<
  K extends keyof Collections
> extends DatastoreCollection<K> {
  constructor(valueChanges$: Rx.Observable<ReadonlyArray<Collections[K]>>) {
    super(
      Rx.of({} as Reference<K>),
      {} as StoreBackend,
      Rx.of({} as RequestStatus<K>),
      valueChanges$,
      Rx.NEVER, // FIXME
    );
  }
}

export class DatastoreDocumentMock<
  K extends keyof Collections
> extends DatastoreDocument<K> {
  constructor(
    valueChanges$: Rx.Observable<Collections[K]>,
    status$: Rx.Observable<RequestStatus<K>> = Rx.of({ ready: true }),
  ) {
    super(
      Rx.of({} as Reference<K> & { readonly path: { readonly ids: [string] } }),
      {} as StoreBackend,
      status$,
      valueChanges$,
    );
  }
}
